import { useState } from "react";
import Sidebar from "../../components/feature/Sidebar";
import TopBar from "../../components/feature/TopBar";
import HubTaskPanel from "../../components/feature/HubTaskPanel";
import { foundationTasks, foundationCollabTasks } from "../../mocks/hubTasks";

const allFoundationTasks = [...foundationTasks, ...foundationCollabTasks];
import { useCompany } from "../../contexts/CompanyContext";

type Tab = "tasks" | "projects" | "grants" | "donors";

const projects = [
  { name: "청년 예술가 창작 지원 사업", budget: 12000, spent: 8400, beneficiaries: 42, status: "진행중", deadline: "2026.06.30" },
  { name: "지역 문화 예술 교육 프로그램", budget: 8000, spent: 6200, beneficiaries: 180, status: "진행중", deadline: "2026.05.31" },
  { name: "장애 예술인 전시 지원", budget: 5000, spent: 1200, beneficiaries: 15, status: "기획중", deadline: "2026.09.30" },
  { name: "다문화 가족 문화 체험", budget: 3500, spent: 3500, beneficiaries: 320, status: "완료", deadline: "2026.03.15" },
];

const grants = [
  { agency: "문화체육관광부", program: "문화예술진흥기금", amount: 25000, status: "수주완료", period: "2026 상반기" },
  { agency: "서울시", program: "생활문화 활성화 지원", amount: 8000, status: "수주완료", period: "2026년도" },
  { agency: "한국문화예술위원회", program: "예술창작지원사업", amount: 15000, status: "신청중", period: "2026 하반기" },
  { agency: "교육부", program: "학교예술강사지원", amount: 6500, status: "검토중", period: "2026년도" },
];

const donors = [
  { name: "삼성문화재단", type: "기업", amount: 10000, date: "2026.01.15", recurring: true },
  { name: "LG 사회공헌팀", type: "기업", amount: 5000, date: "2026.02.01", recurring: true },
  { name: "김○○ 개인 후원자", type: "개인", amount: 1500, date: "2026.03.10", recurring: false },
  { name: "문화나눔클럽", type: "단체", amount: 3000, date: "2026.02.28", recurring: true },
  { name: "박○○ 개인 후원자", type: "개인", amount: 800, date: "2026.03.20", recurring: false },
];

export default function FoundationHub() {
  const { workspaceId } = useCompany();
  const [activeTab, setActiveTab] = useState<Tab>("tasks");

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: "tasks", label: "내 할 일", icon: "ri-checkbox-multiple-line" },
    { id: "projects", label: "지원 사업 현황", icon: "ri-community-line" },
    { id: "grants", label: "보조금 수주 관리", icon: "ri-government-line" },
    { id: "donors", label: "후원자 CRM", icon: "ri-heart-line" },
  ];

  const totalBeneficiaries = projects.reduce((s, p) => s + p.beneficiaries, 0);
  const totalGrantSecured = grants.filter(g => g.status === "수주완료").reduce((s, g) => s + g.amount, 0);
  const totalDonations = donors.reduce((s, d) => s + d.amount, 0);

  return (
    <div className="min-h-screen bg-[#0C0C0E] flex font-sans">
      <Sidebar />
      <div className="flex-1 ml-56 flex flex-col">
        <TopBar />
        <main className="flex-1 p-5">
          {/* Header */}
          <div className="bg-[#111112] border border-white/[0.06] rounded-xl px-5 py-4 flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#4B6CF7]/10 border border-[#4B6CF7]/20">
                <i className="ri-community-line text-[#7A9BFF] text-lg"></i>
              </div>
              <div>
                <h1 className="text-[#F2F2F2] font-semibold text-sm">사업관리 허브</h1>
                <p className="text-[#444444] text-xs">문화예술 지원사업 · 보조금 수주 · 후원자 CRM</p>
              </div>
              <span className="text-[9px] px-2 py-0.5 rounded-full border text-[#7A9BFF] border-[#4B6CF7]/30 bg-[#4B6CF7]/10 ml-1">비영리</span>
            </div>
            <div className="flex items-center gap-5">
              <div className="text-right">
                <div className="text-[#3D3D3D] text-[10px] uppercase tracking-widest">총 수혜자</div>
                <div className="text-[#F2F2F2] font-semibold text-base">{totalBeneficiaries.toLocaleString()}명</div>
              </div>
              <div className="text-right">
                <div className="text-[#3D3D3D] text-[10px] uppercase tracking-widest">보조금 수주</div>
                <div className="text-[#888888] font-semibold text-base">{totalGrantSecured.toLocaleString()}만원</div>
              </div>
              <div className="text-right">
                <div className="text-[#3D3D3D] text-[10px] uppercase tracking-widest">후원금 합계</div>
                <div className="text-[#888888] font-semibold text-base">{totalDonations.toLocaleString()}만원</div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-0.5 mb-5 bg-white/[0.03] rounded-xl p-1 w-fit border border-white/[0.05]">
            {tabs.map(tab => (
              <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab.id ? "bg-[#4B6CF7]/15 text-[#7A9BFF] border border-[#4B6CF7]/20" : "text-[#444444] hover:text-[#888888]"
                }`}>
                <i className={`${tab.icon} text-sm`}></i>{tab.label}
              </button>
            ))}
          </div>

          {activeTab === "tasks" && <HubTaskPanel tasks={allFoundationTasks} hubTitle="사업담당" workspaceId={workspaceId} hub="foundation" />}

          {activeTab === "projects" && (
            <div className="space-y-3">
              {projects.map((p) => {
                const pct = Math.round((p.spent / p.budget) * 100);
                return (
                  <div key={p.name} className="bg-[#111112] border border-white/[0.06] rounded-xl p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="text-[#F2F2F2] text-sm font-semibold">{p.name}</div>
                        <div className="text-[#3D3D3D] text-xs mt-1">마감: {p.deadline}</div>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded border ${
                        p.status === "진행중" ? "text-[#7A9BFF] bg-[#4B6CF7]/10 border-[#4B6CF7]/20" :
                        p.status === "완료" ? "text-[#888888] border-white/10" :
                        "text-[#555555] border-white/[0.05]"
                      }`}>{p.status}</span>
                    </div>
                    <div className="flex items-center gap-6 mb-3">
                      <div>
                        <div className="text-[#3D3D3D] text-[10px]">사업예산</div>
                        <div className="text-[#888888] text-sm font-medium">{p.budget.toLocaleString()}만원</div>
                      </div>
                      <div>
                        <div className="text-[#3D3D3D] text-[10px]">집행금액</div>
                        <div className="text-[#CCCCCC] text-sm font-medium">{p.spent.toLocaleString()}만원</div>
                      </div>
                      <div>
                        <div className="text-[#3D3D3D] text-[10px]">수혜자 수</div>
                        <div className="text-[#7A9BFF] text-sm font-medium">{p.beneficiaries}명</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-white/[0.04] rounded-full h-1">
                        <div className="h-1 rounded-full bg-[#4B6CF7]/50" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-[#444444] text-[11px]">{pct}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === "grants" && (
            <div className="bg-[#111112] border border-white/[0.06] rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-white/[0.04]">
                    {["기관", "사업명", "금액", "기간", "상태"].map(h => (
                      <th key={h} className="px-5 py-3 text-left text-[#3D3D3D] font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.03]">
                  {grants.map((g) => (
                    <tr key={g.program} className="hover:bg-white/[0.02]">
                      <td className="px-5 py-3 text-[#888888]">{g.agency}</td>
                      <td className="px-5 py-3 text-[#CCCCCC] font-medium">{g.program}</td>
                      <td className="px-5 py-3 text-[#888888] tabular-nums">{g.amount.toLocaleString()}만원</td>
                      <td className="px-5 py-3 text-[#555555]">{g.period}</td>
                      <td className="px-5 py-3">
                        <span className={`text-[10px] px-2 py-0.5 rounded border ${
                          g.status === "수주완료" ? "text-[#7A9BFF] bg-[#4B6CF7]/10 border-[#4B6CF7]/20" :
                          g.status === "신청중" ? "text-[#CCCCCC]/40 bg-white/[0.04] border-white/10" :
                          "text-[#444444] border-white/[0.05]"
                        }`}>{g.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === "donors" && (
            <div className="bg-[#111112] border border-white/[0.06] rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-white/[0.05] flex items-center justify-between">
                <span className="text-[#888888] text-xs font-medium uppercase tracking-widest">후원자 명단</span>
                <span className="text-[#3D3D3D] text-xs">총 {donors.length}명 · {totalDonations.toLocaleString()}만원</span>
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-white/[0.04]">
                    {["후원자", "유형", "금액", "일자", "정기후원"].map(h => (
                      <th key={h} className="px-5 py-3 text-left text-[#3D3D3D] font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.03]">
                  {donors.map((d) => (
                    <tr key={d.name} className="hover:bg-white/[0.02]">
                      <td className="px-5 py-3 text-[#CCCCCC] font-medium">{d.name}</td>
                      <td className="px-5 py-3 text-[#555555]">{d.type}</td>
                      <td className="px-5 py-3 text-[#888888] tabular-nums">{d.amount.toLocaleString()}만원</td>
                      <td className="px-5 py-3 text-[#444444]">{d.date}</td>
                      <td className="px-5 py-3">
                        {d.recurring
                          ? <span className="text-[#7A9BFF] text-[10px]"><i className="ri-repeat-line mr-1"></i>정기</span>
                          : <span className="text-[#333333] text-[10px]">일시</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
